// The staff ingress check's browser half (#10718).
//
// When a Finpilot staff member puts content into a client organization and
// the server's check flags it as another client's, the route answers 428
// `staff_ingress_flagged` naming the flagged items (and nothing about why)
// and stores nothing. `fpFetch` recognizes that refusal and asks here; the
// app mounts one `StaffIngressDialogHost`, which registers itself as the
// confirmer, shows the modal, and on "Add anyway" records a grace on the server before
// answering true. `fpFetch` then re-sends the original request unchanged:
// the grace is server-side, so it covers every tab and every route for its
// lifetime.
//
// Modals open one at a time; refusals wait their turn. A refusal for an org
// whose grace was just recorded is answered true without asking. A cancel
// answers only the action it was asked about: every flagged action is its
// own decision.
import store from "@/redux/store";
import type { StaffGrant } from "@/hooks/useStaffGrant";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";

export const STAFF_INGRESS_CODE = "staff_ingress_flagged";

const PREFLIGHT_URL = `${BASE_API_URL_V2}/api/v2/staff/ingress-preflight`;
// How many of a batch's files go to the server as samples, and how large
// one may be. Mirrors the server.
const PREFLIGHT_SAMPLE_FILES = 6;
const PREFLIGHT_SAMPLE_BYTES = 4 * 1024 * 1024;

/** Whether identity says the user is staff inside a client org: the only
 * case the check applies to, so the only case worth a preflight. */
export function inClientEnvironment(): boolean {
  const environment =
    (
      store.getState().userData as
        | { clientEnvironment?: StaffGrant | null }
        | undefined
    )?.clientEnvironment ?? null;
  return environment !== null;
}

export interface StaffIngressPreflight {
  /** False only when the staff member declined the batch in the modal. */
  proceed: boolean;
  /** The token each file of the batch sends as `ingress_batch`, so the
   * server lets it through unread (the preflight saw the batch); null
   * when nothing was checked. */
  batch: string | null;
}

/**
 * One staff ingress check for a whole batch before a single item is
 * sent: every name in the manifest plus a handful of the files as
 * samples, one model call at most on the server. Outside a client
 * environment it sends nothing. A 428 that reaches here is a refusal the
 * staff member already declined in the modal (fpFetch answers it first):
 * the caller sends nothing. Any other failure lets the caller proceed;
 * the server still checks each item on its own.
 */
export async function preflightStaffIngress(
  kind: string,
  items: readonly { name: string; file: File | Blob }[]
): Promise<StaffIngressPreflight> {
  if (!inClientEnvironment() || items.length === 0) {
    return { proceed: true, batch: null };
  }
  const formData = new FormData();
  formData.append("kind", kind);
  formData.append("names", JSON.stringify(items.map((i) => i.name)));
  items
    .filter((i) => i.file.size <= PREFLIGHT_SAMPLE_BYTES)
    .slice(0, PREFLIGHT_SAMPLE_FILES)
    .forEach((i) => formData.append("samples", i.file, i.name));
  try {
    const response = await fpFetch(PREFLIGHT_URL, {
      method: "POST",
      body: formData,
    });
    if (response?.status === 428) return { proceed: false, batch: null };
    if (!response?.ok) return { proceed: true, batch: null };
    const body = (await response.json()) as { batch?: unknown };
    return {
      proceed: true,
      batch: typeof body.batch === "string" ? body.batch : null,
    };
  } catch {
    return { proceed: true, batch: null };
  }
}

/** The refusal. `flagged` names the items the check flagged, a subset of
 * `items`. The server sends nothing about why: not which client, not
 * what matched, not the model's reasoning. */
export interface StaffIngressRefusal {
  code: typeof STAFF_INGRESS_CODE;
  organization_id: string;
  organization_name: string;
  kind: string;
  items: string[];
  flagged: string[];
}

export type StaffIngressConfirmer = (
  refusal: StaffIngressRefusal
) => Promise<boolean>;

/** The clock the rules read; tests pass their own. */
export type Clock = () => number;

let confirmer: StaffIngressConfirmer | null = null;
// Grace expiries the host recorded, by org; a refusal inside one needs no
// modal. A mirror of the server's row for this page, not a cache of
// server data the page reads: the server is asked again on every write.
const graceUntil = new Map<string, number>();
let queue: Promise<unknown> = Promise.resolve();

/** Forget every answer given so far (tests, stories, sign-out). */
export function resetStaffIngress(): void {
  graceUntil.clear();
  queue = Promise.resolve();
}

/** The host registers on mount and unregisters (null) on unmount. */
export function setStaffIngressConfirmer(
  next: StaffIngressConfirmer | null
): void {
  confirmer = next;
  if (next === null) resetStaffIngress();
}

/** The host calls this once the server recorded a grace. */
export function recordStaffIngressGrace(
  organizationId: string,
  expiresAt: string
): void {
  const at = new Date(expiresAt).getTime();
  if (!Number.isNaN(at)) graceUntil.set(organizationId, at);
}

/** The refusal a 428 body carries, or null when the body is something else. */
export function parseStaffIngressRefusal(
  body: unknown
): StaffIngressRefusal | null {
  if (typeof body !== "object" || body === null) return null;
  const b = body as Record<string, unknown>;
  if (b.code !== STAFF_INGRESS_CODE) return null;
  if (
    typeof b.organization_id !== "string" ||
    typeof b.organization_name !== "string" ||
    typeof b.kind !== "string" ||
    !Array.isArray(b.items) ||
    !Array.isArray(b.flagged)
  ) {
    return null;
  }
  return {
    code: STAFF_INGRESS_CODE,
    organization_id: b.organization_id,
    organization_name: b.organization_name,
    kind: b.kind,
    items: b.items.filter((i): i is string => typeof i === "string"),
    flagged: b.flagged.filter((i): i is string => typeof i === "string"),
  };
}

/** True while the page knows of a live grace for the org. */
function underGrace(organizationId: string, now: number): boolean {
  const grace = graceUntil.get(organizationId);
  return grace !== undefined && now < grace;
}

/** Answer a refusal: from what the page knows when it applies, otherwise by
 * asking. Resolves false with no host mounted, so a page without the modal
 * fails the action rather than sending it unchecked. */
export function requestStaffIngressGrace(
  refusal: StaffIngressRefusal,
  clock: Clock = Date.now
): Promise<boolean> {
  const org = refusal.organization_id;
  if (underGrace(org, clock())) return Promise.resolve(true);
  if (confirmer === null) return Promise.resolve(false);
  const answer = queue.then(async () => {
    // A grace recorded while this one waited its turn covers it.
    if (underGrace(org, clock())) return true;
    const ask = confirmer;
    if (ask === null) return false;
    return ask(refusal);
  });
  queue = answer.catch(() => undefined);
  return answer;
}
